/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtergroep.component.ts
 * Gegenereerd met Junie (AI), 30-09-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Recursieve component voor het afbeelden en bewerken van filtergroepen (EN/OF) met kinderen.
 */

import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgSelectModule} from '@ng-select/ng-select';
import {FilterGroep, isFilterGroepGeldig} from '../../../services/filtertool/filtertool-structuur';
import {Filtertoolregel, FiltertoolRegelTypeNamen, FiltertoolRegelTypes} from '../../../services/filtertool/filtertool-regels';
import {FilterregelProjectEditComponent} from '../editors/filterregel-project-edit.component';
import {FilterregelPersoneelEditComponent} from '../editors/filterregel-personeel-edit.component';
import {FilterregelVrijEditComponent} from '../editors/filterregel-vrij-edit.component';
import {FilterregelFunctieEditComponent} from '../editors/filterregel-functie-edit.component';
import {FilterregelFunctiealiasEditComponent} from '../editors/filterregel-functionalias-edit.component';
import {FilterregelPersoneelslabelEditComponent} from '../editors/filterregel-personeelslabel-edit.component';
import {FilterregelOngekwalificeerdEditComponent} from '../editors/filterregel-ongekwalificeerd-edit.component';
import {BaseComponent} from '../../../components/basecomponent';

/**
 * Component die een filtergroep (EN of OF) rendert met zijn kinderen en recursief subgroepen toont.
 * Biedt UI om regels of subgroepen toe te voegen en om clausules te verwijderen.
 */
@Component({
    standalone: true,
    selector: 'filtergroep',
    templateUrl: './filtergroep.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, NgSelectModule, FilterregelProjectEditComponent, FilterregelPersoneelEditComponent, FilterregelVrijEditComponent, FilterregelFunctieEditComponent, FilterregelFunctiealiasEditComponent, FilterregelPersoneelslabelEditComponent, FilterregelOngekwalificeerdEditComponent]
})
export class FiltergroepComponent extends BaseComponent {
    // Inputs (properties)
    /** De groep die op dit niveau wordt gerenderd. */
    @Input({required: true}) groep!: FilterGroep;
    /**
     * Css-selector van de container waarin de keuzelijst van de typekeuze geplaatst wordt.
     * Komt van het scherm (de body van het window), zodat de lijst meeschuift met het window.
     */
    @Input() dropdownContainer: string = 'body';

    // Outputs
    /** Meldt een gewijzigde regel door. */
    @Output() wijzigRegel = new EventEmitter<Filtertoolregel>();
    /** Verzoekt om verwijderen van clausule met id (regel of groep). */
    @Output() verwijderClausule = new EventEmitter<string>();
    /** Verzoekt om een nieuwe regel of subgroep toe te voegen in deze groep. */
    @Output() voegNieuweRegelToeInGroep = new EventEmitter<{ groepId: string; type: (typeof FiltertoolRegelTypes)[number] | '--EN' | '--OF' }>();
    /** Verzoekt om verplaatsing van een regel naar de bovenliggende groep. */
    @Output() verplaatsRegelNaarParent = new EventEmitter<string>();
    /** Verzoekt om de regel in een nieuwe groep te wikkelen met opgegeven operator. */
    @Output() wikkelRegelInGroep = new EventEmitter<{ clausuleId: string; operator: 'en' | 'of' }>();

    // Properties
    /** Lijst met beschikbare regeltypes voor de UI. */
    public readonly regelTypes = FiltertoolRegelTypes;
    /** Mapping van regeltype naar label voor weergave. */
    public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
    /** Lokale keuze voor het nieuw toe te voegen type binnen deze groep. */
    public nieuwRegelType: (typeof FiltertoolRegelTypes)[number] | '--EN' | '--OF' | null = null;

    constructor() {
        super('debug');
    }

    // Public methods
    /**
     * Geeft terug of de meegegeven groep (inclusief subgroepen) geldig is.
     */
    public groepIsGeldig(g: FilterGroep): boolean {
        return isFilterGroepGeldig(g);
    }

    /**
     * Emit een verzoek om een nieuwe regel of subgroep toe te voegen met de lokaal gekozen typewaarde.
     * Reset daarna de lokale selectie voor een nette UX.
     */
    public voegNieuweRegelToe(): void {
        if (!this.nieuwRegelType) { return; }
        this.voegNieuweRegelToeInGroep.emit({ groepId: this.groep.id, type: this.nieuwRegelType });
        this.nieuwRegelType = null;
    }
}
